<template>
    <div v-if="visible && item" class="listener-track-sheet">
        <button class="listener-track-sheet__scrim" aria-label="close" @click="close"></button>
        <div
            :class="['listener-track-sheet__panel', isDragging ? 'is-dragging' : '']"
            :style="panelStyle"
            @touchstart.passive="handlePanelTouchStart"
            @touchmove.prevent="handlePanelTouchMove"
            @touchend="handlePanelTouchEnd"
            @touchcancel="handlePanelTouchCancel"
        >
            <div class="listener-track-sheet__handle"></div>

            <template v-if="mode === 'actions'">
                <div class="listener-track-sheet__hero">
                    <img
                        :src="item.image || '/static/dist/img/album_null.jpg'"
                        :alt="item.title"
                        class="listener-track-sheet__cover"
                    >
                    <div class="listener-track-sheet__meta">
                        <div class="listener-track-sheet__eyebrow">{{ $tt('歌曲操作') }}</div>
                        <div class="listener-track-sheet__title">{{ item.title }}</div>
                        <div class="listener-track-sheet__sub">{{ item.artist || item.album || $tt('未知艺术家') }}</div>
                    </div>
                    <button class="listener-track-sheet__close" @click="close">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.7 2.88 18.3 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.3z"></path>
                        </svg>
                    </button>
                </div>

                <div class="listener-track-sheet__section">
                    <button
                        v-for="action in primaryActions"
                        :key="action.key"
                        class="listener-track-sheet__action"
                        @click="handleAction(action.key)"
                    >
                        <span :class="['listener-track-sheet__icon', action.danger ? 'is-danger' : '']">
                            <svg v-if="action.key === 'next'" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M4 6h11v2H4zm0 5h16v2H4zm0 5h16v2H4zm13.5-8.5L21 11l-3.5 3.5V12H14v-2h3.5z"></path>
                            </svg>
                            <svg v-else-if="action.key === 'queue'" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M4 6h12v2H4zm0 5h12v2H4zm0 5h8v2H4zm13-1v-3h-3v-2h3V7h2v3h3v2h-3v3z"></path>
                            </svg>
                            <svg v-else-if="action.key === 'playlist'" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M4 6h10v2H4zm0 5h10v2H4zm0 5h6v2H4zm11-6V7h2v3h3v2h-3v3h-2v-3h-3v-2z"></path>
                            </svg>
                            <svg v-else-if="action.key === 'favourite'" viewBox="0 0 24 24" :fill="item.favourite ? '#ef4444' : 'none'" stroke="currentColor" stroke-width="2">
                                <path d="M12 21s-6.72-4.35-9.2-8.3A5.38 5.38 0 0 1 7.35 4.6 5.2 5.2 0 0 1 12 7.09 5.2 5.2 0 0 1 16.65 4.6a5.38 5.38 0 0 1 4.55 8.1C18.72 16.65 12 21 12 21z"></path>
                            </svg>
                            <svg v-else-if="action.key === 'removeQueue'" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M5 11h14v2H5z"></path>
                            </svg>
                            <svg v-else-if="action.key === 'removePlaylist'" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M4 6h16v2H4zm0 5h12v2H4zm0 5h8v2H4zm10-1v-2h6v2z"></path>
                            </svg>
                        </span>
                        <span class="listener-track-sheet__copy">
                            <span class="listener-track-sheet__label">{{ action.label }}</span>
                            <span class="listener-track-sheet__desc">{{ action.desc }}</span>
                        </span>
                    </button>
                </div>

                <div v-if="adminActions.length" class="listener-track-sheet__section">
                    <div class="listener-track-sheet__section-title">{{ $tt('管理操作') }}</div>
                    <button
                        v-for="action in adminActions"
                        :key="action.key"
                        class="listener-track-sheet__action"
                        @click="handleAction(action.key)"
                    >
                        <span :class="['listener-track-sheet__icon', action.danger ? 'is-danger' : '']">
                            <svg v-if="action.key === 'editPage' || action.key === 'metadata'" viewBox="0 0 24 24" fill="currentColor">
                                <path d="m16.86 3.49 3.65 3.65-9.9 9.9H6.96v-3.65l9.9-9.9zm1.41-1.41a2 2 0 0 1 2.83 0l1.8 1.8a2 2 0 0 1 0 2.83l-1.07 1.07-3.65-3.65 1.09-1.05zM4 19h16v2H4z"></path>
                            </svg>
                            <svg v-else-if="action.key === 'delete'" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v8h-2V9zm4 0h2v8h-2V9zM7 9h2v8H7V9z"></path>
                            </svg>
                        </span>
                        <span class="listener-track-sheet__copy">
                            <span class="listener-track-sheet__label">{{ action.label }}</span>
                            <span class="listener-track-sheet__desc">{{ action.desc }}</span>
                        </span>
                    </button>
                </div>
            </template>

            <template v-else-if="mode === 'playlist'">
                <div class="listener-track-sheet__subpanel">
                    <button class="listener-track-sheet__back" @click="mode = 'actions'">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M15 18l-6-6 6-6"></path>
                        </svg>
                        <span>{{ $tt('返回') }}</span>
                    </button>
                    <div class="listener-track-sheet__subhead">
                        <div class="listener-track-sheet__section-title">{{ $tt('添加到播放列表') }}</div>
                        <button class="listener-track-sheet__ghost" @click="isShowCreatePlist = !isShowCreatePlist">
                            {{ isShowCreatePlist ? $tt('取消新建') : $tt('新建列表') }}
                        </button>
                    </div>

                    <div v-if="playlistLoading" class="listener-track-sheet__state">
                        <div class="listener-track-sheet__loading"></div>
                        <span>{{ $tt('正在加载...') }}</span>
                    </div>

                    <div v-else-if="playLists.length" class="listener-track-sheet__playlist-list">
                        <button
                            v-for="playlist in playLists"
                            :key="playlist.id"
                            :class="['listener-track-sheet__playlist-row', String(selectPlaylist) === String(playlist.id) ? 'is-active' : '']"
                            @click="selectPlaylist = playlist.id"
                        >
                            <span>{{ playlist.name }}</span>
                            <svg v-if="String(selectPlaylist) === String(playlist.id)" viewBox="0 0 24 24" fill="currentColor">
                                <path d="m9.55 18-5.5-5.5 1.41-1.41 4.09 4.08 8.99-8.99 1.41 1.42z"></path>
                            </svg>
                        </button>
                    </div>

                    <div v-else class="listener-track-sheet__empty">
                        <div class="listener-track-sheet__empty-title">{{ $tt('还没有播放列表') }}</div>
                        <div class="listener-track-sheet__empty-sub">{{ $tt('新建一个列表后，就可以把这首歌收进去。') }}</div>
                    </div>

                    <div v-if="isShowCreatePlist || !playLists.length" class="listener-track-sheet__create">
                        <input
                            v-model.trim="plName"
                            type="text"
                            :placeholder="$tt('请输入播放列表名称')"
                            class="listener-track-sheet__input"
                        >
                        <button class="listener-track-sheet__submit" :disabled="!plName" @click="createPlaylistFunc">
                            {{ $tt('新建并添加') }}
                        </button>
                    </div>

                    <button
                        v-if="playLists.length"
                        class="listener-track-sheet__submit"
                        :disabled="!selectPlaylist"
                        @click="addToPlaylistFunc"
                    >
                        {{ $tt('添加到所选列表') }}
                    </button>
                </div>
            </template>

            <template v-else-if="mode === 'delete-confirm'">
                <div class="listener-track-sheet__subpanel">
                    <button class="listener-track-sheet__back" @click="mode = 'actions'">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M15 18l-6-6 6-6"></path>
                        </svg>
                        <span>{{ $tt('返回') }}</span>
                    </button>
                    <div class="listener-track-sheet__section-title">{{ $tt('删除音乐') }}</div>
                    <div class="listener-track-sheet__warning">
                        <div>{{ $tt('删除音乐: 将音乐从音乐收藏中移除，同时删除音乐文件。') }}</div>
                        <div>{{ $tt('删除收藏: 将音乐从音乐收藏中移除，但并不会删除音乐文件。') }}</div>
                    </div>
                    <button class="listener-track-sheet__submit listener-track-sheet__submit--danger" @click="deleteMusic('delete_file')">
                        {{ $tt('删除音乐') }}
                    </button>
                    <button class="listener-track-sheet__submit listener-track-sheet__submit--muted" @click="deleteMusic('delete_track')">
                        {{ $tt('删除收藏') }}
                    </button>
                </div>
            </template>

            <div class="listener-track-sheet__bottom-spacer" aria-hidden="true"></div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
    name: 'ListenerTrackActionSheet',
    props: {
        visible: { type: Boolean, default: false },
        item: { type: Object, default: null },
        index: { type: Number, default: -1 }
    },
    data() {
        return {
            mode: 'actions',
            playLists: [],
            selectPlaylist: '',
            plName: '',
            isShowCreatePlist: false,
            playlistLoading: false,
            dragStartY: 0,
            dragOffsetY: 0,
            isDragging: false,
            previousBodyOverflow: ''
        }
    },
    computed: {
        ...mapGetters({
            isSuperuser: 'getIsSuperuser'
        }),
        isInQueue() {
            if (!this.item) {
                return false
            }
            const queue = this.$store.state.common.queue || []
            return queue.some(track => track.id === this.item.id)
        },
        panelStyle() {
            return {
                transform: `translateY(${this.dragOffsetY}px)`
            }
        },
        primaryActions() {
            if (!this.item) {
                return []
            }

            const actions = [
                {
                    key: 'next',
                    label: this.$tt('添加下一首'),
                    desc: this.$tt('紧接当前歌曲播放')
                },
                {
                    key: 'queue',
                    label: this.$tt('添加到队列'),
                    desc: this.$tt('继续留在待播列表里')
                },
                {
                    key: 'playlist',
                    label: this.$tt('添加到播放列表'),
                    desc: this.$tt('收藏进你自己的歌单')
                },
                {
                    key: 'favourite',
                    label: this.item.favourite ? this.$tt('移出喜爱') : this.$tt('添加到喜爱'),
                    desc: this.item.favourite ? this.$tt('从喜爱列表移除') : this.$tt('点亮这首常听歌曲')
                }
            ]

            if (this.isInQueue) {
                actions.push({
                    key: 'removeQueue',
                    label: this.$tt('移出队列'),
                    desc: this.$tt('不再保留在当前待播列表')
                })
            }

            if (this.$route.name === 'playlist') {
                actions.push({
                    key: 'removePlaylist',
                    label: this.$tt('移出播放列表'),
                    desc: this.$tt('从当前列表中移除这首歌')
                })
            }

            return actions
        },
        adminActions() {
            if (!this.isSuperuser || !this.item) {
                return []
            }

            return [
                {
                    key: 'editPage',
                    label: this.$tt('操作台编辑'),
                    desc: this.$tt('跳转到操作台继续处理')
                },
                {
                    key: 'metadata',
                    label: this.$tt('元数据编辑'),
                    desc: this.$tt('修改标题、封面和标签信息')
                },
                {
                    key: 'delete',
                    label: this.$tt('删除音乐'),
                    desc: this.$tt('危险操作，请谨慎使用'),
                    danger: true
                }
            ]
        }
    },
    watch: {
        visible(value) {
            if (value) {
                this.lockBodyScroll()
                return
            }
            this.resetPanelState()
            this.restoreBodyScroll()
        }
    },
    beforeDestroy() {
        this.restoreBodyScroll()
    },
    methods: {
        close() {
            this.$emit('close')
        },
        resetPanelState() {
            this.mode = 'actions'
            this.playLists = []
            this.selectPlaylist = ''
            this.plName = ''
            this.isShowCreatePlist = false
            this.playlistLoading = false
            this.dragOffsetY = 0
            this.isDragging = false
        },
        lockBodyScroll() {
            if (typeof document === 'undefined') {
                return
            }
            this.previousBodyOverflow = document.body.style.overflow
            document.body.style.overflow = 'hidden'
        },
        restoreBodyScroll() {
            if (typeof document === 'undefined') {
                return
            }
            document.body.style.overflow = this.previousBodyOverflow || ''
        },
        handleAction(actionKey) {
            if (!this.item) {
                return
            }

            if (actionKey === 'playlist') {
                this.openPlaylistMode()
                return
            }

            if (actionKey === 'delete') {
                this.mode = 'delete-confirm'
                return
            }

            if (actionKey === 'metadata') {
                this.close()
                this.$emit('open-edit', this.item)
                return
            }

            if (actionKey === 'editPage') {
                this.close()
                if (this.$route.name !== 'home') {
                    this.$router.push({ name: 'home' })
                }
                this.$store.commit('setFullPath', this.item.path)
                return
            }

            if (actionKey === 'next') {
                this.close()
                this.$store.commit('setNextInQueue', [this.item])
                this.notifySuccess(this.$tt('添加成功！'))
                return
            }

            if (actionKey === 'queue') {
                this.close()
                this.$store.commit('addToQueue', [this.item])
                this.notifySuccess(this.$tt('添加成功！'))
                return
            }

            if (actionKey === 'removeQueue') {
                const queue = this.$store.state.common.queue || []
                const trackIndex = queue.findIndex(track => track.id === this.item.id)
                if (trackIndex !== -1) {
                    this.close()
                    this.$store.commit('removeFromQueue', trackIndex)
                    this.notifySuccess(this.$tt('移出成功！'))
                }
                return
            }

            if (actionKey === 'removePlaylist') {
                this.removePlayListFunc(this.index)
                return
            }

            if (actionKey === 'favourite') {
                this.toggleFavouriteTrack(this.item)
            }
        },
        openPlaylistMode() {
            this.mode = 'playlist'
            this.isShowCreatePlist = false
            this.loadPlaylists()
        },
        loadPlaylists() {
            this.playlistLoading = true
            this.$api.subsonic.getPlaylists().then((res) => {
                this.playLists = res
                const savedPlaylist = localStorage.getItem('lastSelectedPlaylist')
                if (savedPlaylist && this.playLists.some(item => String(item.id) === String(savedPlaylist))) {
                    this.selectPlaylist = savedPlaylist
                } else if (this.playLists.length > 0) {
                    this.selectPlaylist = this.playLists[0].id
                } else {
                    this.selectPlaylist = ''
                }
            }).finally(() => {
                this.playlistLoading = false
            })
        },
        createPlaylistFunc() {
            if (!this.plName || !this.item) {
                return
            }
            this.$api.subsonic.createPlaylist(this.plName, [this.item.id]).then(() => {
                this.notifySuccess(this.$tt('添加成功！'))
                this.close()
            })
        },
        addToPlaylistFunc() {
            if (!this.selectPlaylist || !this.item) {
                return
            }
            localStorage.setItem('lastSelectedPlaylist', this.selectPlaylist)
            this.$api.subsonic.addToPlaylist(this.selectPlaylist, [this.item.id]).then(() => {
                this.notifySuccess(this.$tt('添加成功！'))
                this.close()
            })
        },
        removePlayListFunc(index) {
            this.$api.subsonic.removeFromPlaylist(this.$route.params.id, index).then(() => {
                this.notifySuccess(this.$tt('删除成功！'))
                this.close()
                this.$emit('remove-from-playlist', index)
            })
        },
        toggleFavouriteTrack(item) {
            const request = item.favourite
                ? this.$api.subsonic.removeFavourite(item.id, 'track')
                : this.$api.subsonic.addFavourite(item.id, 'track')

            request.then(() => {
                item.favourite = !item.favourite
                this.close()
            })
        },
        deleteMusic(mode) {
            if (!this.item) {
                return
            }
            this.$api.Task.deleteTrack({ track_id: this.item.id, mode }).then((res) => {
                if (!res.result) {
                    return
                }
                this.notifySuccess(this.$tt('删除成功！'))
                this.close()
                this.$emit('delete-track-success', this.index)
            })
        },
        notifySuccess(title) {
            this.$myMsg.notify({
                title,
                type: 'success',
                time: 2000
            })
        },
        handlePanelTouchStart(event) {
            this.dragStartY = event.touches[0].clientY
            this.dragOffsetY = 0
            this.isDragging = true
        },
        handlePanelTouchMove(event) {
            const currentY = event.touches[0].clientY
            this.dragOffsetY = Math.max(currentY - this.dragStartY, 0)
        },
        handlePanelTouchEnd() {
            const shouldClose = this.dragOffsetY > 96
            this.isDragging = false
            this.dragOffsetY = 0
            if (shouldClose) {
                this.close()
            }
        },
        handlePanelTouchCancel() {
            this.isDragging = false
            this.dragOffsetY = 0
        }
    }
}
</script>

<style scoped>
.listener-track-sheet {
    position: fixed;
    inset: 0;
    z-index: 1200;
}

.listener-track-sheet__scrim {
    position: absolute;
    inset: 0;
    border: 0;
    background: rgba(15, 23, 42, 0.24);
    backdrop-filter: blur(4px);
}

.listener-track-sheet__panel {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 0.55rem 0.9rem calc(1rem + env(safe-area-inset-bottom, 0px));
    border-radius: 1.75rem 1.75rem 0 0;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.97), rgba(248, 250, 252, 0.96));
    box-shadow: 0 -12px 40px rgba(15, 23, 42, 0.14);
    transition: transform 0.24s ease;
}

.listener-track-sheet__bottom-spacer {
    height: calc(var(--listener-shell-mini-player-height, 4.75rem) + 0.35rem);
    pointer-events: none;
}

.listener-track-sheet__panel.is-dragging {
    transition: none;
}

.listener-track-sheet__handle {
    width: 2.7rem;
    height: 0.32rem;
    border-radius: 999px;
    background: rgba(148, 163, 184, 0.58);
    margin: 0.1rem auto 0.95rem;
}

.listener-track-sheet__hero {
    display: flex;
    align-items: center;
    gap: 0.9rem;
    margin-bottom: 0.95rem;
}

.listener-track-sheet__cover {
    width: 3.85rem;
    height: 3.85rem;
    object-fit: cover;
    border-radius: 1rem;
    flex-shrink: 0;
    box-shadow: 0 12px 28px rgba(148, 163, 184, 0.16);
}

.listener-track-sheet__meta {
    min-width: 0;
    flex: 1;
}

.listener-track-sheet__eyebrow {
    font-size: 0.78rem;
    font-weight: 700;
    color: #94a3b8;
}

.listener-track-sheet__title,
.listener-track-sheet__sub {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.listener-track-sheet__title {
    margin-top: 0.22rem;
    font-size: 1rem;
    font-weight: 800;
    color: #111827;
}

.listener-track-sheet__sub {
    margin-top: 0.2rem;
    font-size: 0.82rem;
    color: #64748b;
}

.listener-track-sheet__close,
.listener-track-sheet__ghost {
    border: 0;
    background: rgba(255, 255, 255, 0.88);
    color: #111827;
    border-radius: 999px;
    box-shadow: 0 10px 24px rgba(148, 163, 184, 0.12);
}

.listener-track-sheet__close {
    width: 2.65rem;
    height: 2.65rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.listener-track-sheet__close svg {
    width: 1rem;
    height: 1rem;
}

.listener-track-sheet__section + .listener-track-sheet__section {
    margin-top: 0.9rem;
}

.listener-track-sheet__section-title {
    margin-bottom: 0.55rem;
    font-size: 0.82rem;
    font-weight: 700;
    color: #94a3b8;
}

.listener-track-sheet__action {
    width: 100%;
    border: 0;
    background: rgba(255, 255, 255, 0.76);
    border-radius: 1.3rem;
    box-shadow: 0 12px 28px rgba(148, 163, 184, 0.08);
    padding: 0.85rem 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.85rem;
    text-align: left;
}

.listener-track-sheet__action + .listener-track-sheet__action {
    margin-top: 0.55rem;
}

.listener-track-sheet__icon {
    width: 2.6rem;
    height: 2.6rem;
    border-radius: 999px;
    background: rgba(241, 245, 249, 0.92);
    color: #111827;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.listener-track-sheet__icon.is-danger {
    background: rgba(254, 226, 226, 0.92);
    color: #dc2626;
}

.listener-track-sheet__icon svg {
    width: 1.12rem;
    height: 1.12rem;
}

.listener-track-sheet__copy {
    min-width: 0;
    flex: 1;
}

.listener-track-sheet__label {
    display: block;
    font-size: 0.94rem;
    font-weight: 700;
    color: #111827;
}

.listener-track-sheet__desc {
    display: block;
    margin-top: 0.18rem;
    font-size: 0.78rem;
    color: #64748b;
}

.listener-track-sheet__subpanel {
    padding-bottom: 0.2rem;
}

.listener-track-sheet__back {
    border: 0;
    background: transparent;
    color: #111827;
    display: inline-flex;
    align-items: center;
    gap: 0.28rem;
    padding: 0.15rem 0;
    font-size: 0.85rem;
    font-weight: 700;
}

.listener-track-sheet__back svg {
    width: 1rem;
    height: 1rem;
}

.listener-track-sheet__subhead {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin: 0.55rem 0 0.75rem;
}

.listener-track-sheet__ghost {
    min-height: 2.2rem;
    padding: 0 0.85rem;
    font-size: 0.78rem;
    font-weight: 700;
}

.listener-track-sheet__playlist-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.listener-track-sheet__playlist-row {
    width: 100%;
    border: 0;
    padding: 0.85rem 0.95rem;
    border-radius: 1.2rem;
    background: rgba(255, 255, 255, 0.78);
    color: #111827;
    box-shadow: 0 12px 28px rgba(148, 163, 184, 0.08);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    font-size: 0.92rem;
    font-weight: 700;
}

.listener-track-sheet__playlist-row.is-active {
    background: linear-gradient(135deg, rgba(255,255,255,0.95), rgba(226,232,240,0.86));
    box-shadow: 0 16px 34px rgba(148, 163, 184, 0.14);
}

.listener-track-sheet__playlist-row svg {
    width: 1rem;
    height: 1rem;
}

.listener-track-sheet__create {
    margin-top: 0.75rem;
}

.listener-track-sheet__input {
    width: 100%;
    border: 0;
    border-radius: 1.15rem;
    background: rgba(255, 255, 255, 0.88);
    color: #111827;
    padding: 0.95rem 1rem;
    box-shadow: inset 0 0 0 1px rgba(226, 232, 240, 0.9);
}

.listener-track-sheet__submit {
    width: 100%;
    margin-top: 0.75rem;
    min-height: 3.15rem;
    border: 0;
    border-radius: 1.25rem;
    background: #111827;
    color: #fff;
    font-size: 0.92rem;
    font-weight: 700;
    box-shadow: 0 16px 34px rgba(15, 23, 42, 0.18);
}

.listener-track-sheet__submit:disabled {
    opacity: 0.42;
    box-shadow: none;
}

.listener-track-sheet__submit--danger {
    background: #dc2626;
}

.listener-track-sheet__submit--muted {
    background: rgba(255, 255, 255, 0.86);
    color: #111827;
    box-shadow: 0 12px 28px rgba(148, 163, 184, 0.08);
}

.listener-track-sheet__empty,
.listener-track-sheet__state,
.listener-track-sheet__warning {
    border-radius: 1.3rem;
    background: rgba(255, 255, 255, 0.76);
    box-shadow: 0 12px 28px rgba(148, 163, 184, 0.08);
    padding: 1rem;
    color: #64748b;
}

.listener-track-sheet__empty-title {
    font-size: 0.92rem;
    font-weight: 700;
    color: #111827;
}

.listener-track-sheet__empty-sub {
    margin-top: 0.28rem;
    font-size: 0.8rem;
}

.listener-track-sheet__state {
    display: flex;
    align-items: center;
    gap: 0.7rem;
}

.listener-track-sheet__loading {
    width: 1rem;
    height: 1rem;
    border-radius: 999px;
    border: 2px solid rgba(148, 163, 184, 0.3);
    border-top-color: #111827;
    animation: listener-track-sheet-spin 0.8s linear infinite;
}

.listener-track-sheet__warning {
    display: grid;
    gap: 0.55rem;
    margin-top: 0.7rem;
    font-size: 0.82rem;
}

@keyframes listener-track-sheet-spin {
    to {
        transform: rotate(360deg);
    }
}
</style>
